import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { Button, InputGroup, FormControl, Spinner, Card } from "react-bootstrap";
import InfoButtonWithModal from "./InfoButtonModal";
import { useParams } from "react-router-dom";

const UserRecentInterest = ({ user, readOnly = false }) => {
	const [interestText, setInterestText] = useState("");
	const [error, setError] = useState("");
	const [isSaving, setIsSaving] = useState(false);
	const [enableEdit, setEnableEdit] = useState(false);

	const { uid } = useParams();

	// ✅ Prefer user.uid if available
	const finalUid = user?.uid || uid;

	useEffect(() => {
		// ✅ 1. If userData already has interest → use it immediately
		if (user?.interestText) {
			setInterestText(user.interestText);
			return;
		}

		// ✅ 2. Otherwise fetch from Firestore
		if (finalUid) {
			const fetchData = async () => {
				try {
					const userRef = doc(db, "users", finalUid);
					const docSnap = await getDoc(userRef);

					if (docSnap.exists()) {
						const data = docSnap.data();
						setInterestText(data.interestText || "");
					}
				} catch (error) {
					console.error("Error fetching user data:", error);
				}
			};

			fetchData();
		}
	}, [finalUid, user]);

	const handleTextChange = (e) => {
		const value = e.target.value;
		const maxCharacters = 250;

		if (value.length <= maxCharacters) {
			setInterestText(value);
			setError("");
		} else {
			setError(`You can only write a maximum of ${maxCharacters} characters.`);
		}
	};

	const handleInputClick = () => {
		if (!readOnly) setEnableEdit(true);
	};

	const handleClose = () => {
		if (!readOnly) setEnableEdit(false);
	};

	const handleSaveInterest = async () => {
		if (!finalUid) return;

		try {
			setIsSaving(true);

			const userRef = doc(db, "users", finalUid);
			await setDoc(userRef, { interestText }, { merge: true });

			setError("");
		} catch (error) {
			console.error("Error saving interest text:", error);
		} finally {
			setIsSaving(false);
			setEnableEdit(false);
		}
	};

	return (
		<Card className="w-100 my-2 mb-3 shadow-sm">
			<div className="d-flex flex-column p-3">
				<div className="d-flex">
					<InfoButtonWithModal enableEdit={enableEdit} />

					{!enableEdit || readOnly ? (
						<div
							className="fst-italic lh-sm text-start w-100 position-relative ps-4 pe-3"
							style={{ cursor: readOnly ? "default" : "pointer" }}
							onClick={handleInputClick}
						>
							{interestText || "Write something about yourself here..."}

							{!readOnly && (
								<span
									className="position-absolute my-btn rounded-circle"
									style={{
										top: "-20px",
										right: "-25px",
										width: "30px",
										height: "30px",
										cursor: "pointer",
										transform: "translate(20%, -20%)",
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
									}}
									onClick={handleInputClick}
								>
									<i className="bi bi-pencil-fill" style={{ fontSize: "1rem" }}></i>
								</span>
							)}
						</div>
					) : (
						<div className="w-100 position-relative">
							<span
								className="position-absolute m-2"
								style={{ cursor: "pointer", fontSize: "1.2rem", zIndex: 1, top: "-30px", right: "-26px" }}
								onClick={handleClose}
							>
								<i className="bi bi-x-lg"></i>
							</span>

							<InputGroup className="mb-2">
								<FormControl
									as="textarea"
									rows={3}
									placeholder="What interested you recently?"
									value={interestText}
									onChange={handleTextChange}
									maxLength="200"
								/>
							</InputGroup>

							<Button className="my-btn w-100 mb-2" onClick={handleSaveInterest} disabled={isSaving}>
								{isSaving ? <Spinner animation="border" size="sm" /> : "Save"}
								<i className="bi bi-star-fill ms-2" style={{ color: "#ffc107" }}></i>
							</Button>

							{error && <div className="alert alert-danger mt-2">{error}</div>}
						</div>
					)}
				</div>
			</div>
		</Card>
	);
};

export default UserRecentInterest;
